import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { RESERVATION_HOURS_MAX } from 'src/app/config/reservation-hours.constant';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CourseCreateDto,
  HashtagCreateDto,
  HashtagType,
  Product,
  ProductRepresentationPhotoCreateDto,
  ProductStatus,
  ProductUpdateDto,
} from 'src/app/model/product';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { GeocodeService } from 'src/app/service/geocode.service';
import { AuthService } from 'src/app/service/auth.service';
import { FormService } from 'src/app/service/form.service';
import { MessageService } from 'src/app/service/message.service';
import { ProductService } from 'src/app/service/product.service';
import {
  MobileVerificationBottomSheetComponent,
  MobileVerificationBottomSheetData,
  MobileVerificationResult,
} from 'src/app/component/mobile-verification-bottom-sheet/mobile-verification-bottom-sheet.component';
import { HostInfo } from 'src/app/model/user';
import { hasThemeSelectionChanged, toThemeUpdateErrorMessage } from 'src/app/util/product-theme';
import { PhotoUploader, toStoredPhotoUrls } from 'src/app/util/photo-upload';
import { captureSurfacedFailure } from 'src/app/util/sentry-capture';
import { failIfSwallowed } from 'src/app/util/swallowed-http';
import {
  dropDescriptionOverRichHtml,
  RICH_HTML_AI_PARTIAL_MESSAGE,
  RICH_HTML_AI_SKIPPED_MESSAGE,
} from 'src/app/util/rich-html';
import { imageResizeOption, S3Service } from 'src/app/service/s3.service';
import { UserService } from 'src/app/service/user.service';
import { UtilService } from 'src/app/service/util.service';
import { environment } from 'src/environments/environment';
import { MeetingControl } from './../meeting-add/meeting-control';
import { AiService, ProductContext } from 'src/app/service/ai.service';
import { findActiveRejection } from 'src/app/util/product-review';

declare var naver: any;

const REVIEW_REQUEST_FAILED = '검수 요청에 실패했습니다. 다시 시도해주세요.';
const FIXED_HEIGHT_PHOTO_RESIZE: imageResizeOption = { height: 800, fit: 'cover' };
const SQUARE_PHOTO_RESIZE: imageResizeOption = { width: 800, height: 800, fit: 'cover' };

/**
 * 검수 요청 실패 사유를 호스트에게 보여줄 문구로 바꾼다.
 *
 * 400만 서버 문구를 노출한다 — 연락처 미인증처럼 호스트가 직접 해결할 수 있는 사유를 서버가
 * 한국어로 담아 주는 유일한 상태다. 401·5xx는 영문 내부 메시지라 그대로 띄우면 의미가 없다.
 * Next.js 위저드(`ReviewStep.tsx`)와 같은 규칙 — 두 화면이 같은 엔드포인트를 쓰므로 서버 가드에
 * 걸렸을 때 한쪽만 사유를 알려주면 이 화면의 호스트는 원인을 모른 채 재시도만 반복하게 된다.
 */
function toReviewRequestErrorMessage(error: unknown): string {
  if (!(error instanceof HttpErrorResponse) || error.status !== 400) return REVIEW_REQUEST_FAILED;
  const message = error.error?.message;
  const text = Array.isArray(message) ? message[0] : message;
  return typeof text === 'string' && text.trim() ? text.trim() : REVIEW_REQUEST_FAILED;
}

@Component({
  selector: 'meeting-edit',
  templateUrl: './meeting-edit.page.html',
  styleUrls: ['./meeting-edit.page.scss'],
})
export class MeetingEditPage extends MeetingControl implements OnInit, AfterViewInit, OnDestroy {
  meeting: Product;
  productContext?: ProductContext;
  aiAdvisorEnabled = false;
  // ai-chat 은 좌측 하단 fixed 인데 host 의 mat-sidenav-content 가 z-index stacking
  // context 를 만들어 좌측 메뉴에 가려진다. 앵커를 body 로 끌어올려 stacking 탈출.
  @ViewChild('aiChatAnchor') private aiChatAnchor?: ElementRef<HTMLElement>;
  private aiChatMovedToBody = false;
  private aiChatMoveTimer?: ReturnType<typeof setTimeout>;
  private isDestroyed = false;
  private autoSaveSubscription: Subscription;
  private formChangeSubscription: Subscription;
  isAutoSaving = false;
  lastAutoSaved: Date | null = null;
  hasUnsavedChanges = false;
  private formRevision = 0;
  private autoSavePending = false;
  private autoSaveFailureNotified = false;
  private initialFormValue: any;
  ProductStatus = ProductStatus;
  /**
   * 호스트 연락처(host_info.mobile) 인증 여부. 검수 요청 전 안내 노출에만 쓴다.
   * 조회 전/실패 시 true라 안내가 뜨지 않는다 — 차단은 서버가 하므로 놓쳐도 팝업으로 사유가 전달된다.
   */
  isMobileVerified = true;
  /** 예약 가능한 일정 보유 여부. 없으면 검수 요청 전에 안내를 띄운다(NNB-679). */
  hasReservableTicket = true;
  private hostInfo?: HostInfo;
  /** 선택된 테마 id. 같은 값을 `themeIds` 폼 컨트롤에도 넣어 자동 저장·이탈 경고가 함께 반응한다. */
  themeIds: number[] = [];
  /**
   * 상세 응답에 `themes` 키가 실려 왔는지. **없으면 테마를 아예 건드리지 않는다.**
   * 화면이 서버보다 먼저 배포되면 구버전 응답에는 키가 통째로 없는데, 이를 "전부 해제"로 읽고
   * 저장하면 제목만 고쳐도 기존 테마가 통째로 지워진다. `undefined`(모름)와 `[]`(전부 해제)를 가른다.
   *
   * 저장하지 않을 거라면 고르게 해서도 안 되므로(선택이 조용히 버려진다) 피커 노출도 이 값이 정한다.
   */
  themesLoaded = false;
  /** 마지막으로 서버와 맞춘 테마. 변경이 없으면 저장 요청을 보내지 않는다. */
  private loadedThemeIds: number[] = [];

  constructor(
    private geocodeService: GeocodeService,
    private formService: FormService,
    private router: Router,
    public ngZone: NgZone,
    public s3Service: S3Service,
    private utilService: UtilService,
    public authService: AuthService,
    private fb: UntypedFormBuilder,
    private productService: ProductService,
    private route: ActivatedRoute,
    private messageService: MessageService,
    public bottomSheet: MatBottomSheet,
    private aiService: AiService,
    private userService: UserService,
  ) {
    super(ngZone, s3Service, authService, bottomSheet);
  }

  ngOnInit() {
    //TODO set list hashtags
    this.productService.searchHashtag({ page: 1, limit: 9999, isAnalysis: false }).subscribe((resp) => {
      this.hashtags = resp.items;
    });
    this.productService.searchAmenity({ page: 1, limit: 9999 }).subscribe((resp) => {
      this.amenities = resp.items;
    });
    this.authService.getCurrentUser().subscribe((user) => {
      this.user = user;
    });
    // 검수 요청이 서버에서 막히기 전에 연락처 미인증을 미리 안내하기 위해 조회한다.
    // 조회가 실패하면 isMobileVerified가 기본값 true로 남아 안내를 띄우지 않는다 — 인증을
    // 마친 호스트에게 잘못된 경고를 보이는 것보다 서버 가드가 걸러 주는 편이 낫다.
    //
    // error 콜백을 두지 않는 건 의도적이다. 콜백을 달면 에러가 구독자에서 끝나 app.module의
    // 전역 ErrorHandler(Sentry)까지 가지 않아, 운영에서 이 조회 실패가 보이지 않게 된다.
    this.userService.getHostInfoByCurrent().subscribe((hostInfo) => {
      this.hostInfo = hostInfo;
      this.isMobileVerified = !!hostInfo?.mobile?.trim();
    });
    this.quillStyle = this.utilService.getQuillStyle();
    this.productForm = this.fb.group({
      version: new UntypedFormControl(''),
      title: new UntypedFormControl('', this.formService.getValidators(50)),
      hashtags: new UntypedFormControl([], Validators.required),
      // 저장 값은 `themeIds` 프로퍼티에서 읽지만(buildProductDto) 폼 컨트롤로도 둔다.
      // 컨트롤 밖에만 두면 valueChanges 가 울리지 않아 자동 저장도, 이탈 경고(productForm.dirty)도
      // 테마 변경을 못 본다 — 호스트가 테마만 고치고 나가면 아무 경고 없이 사라진다.
      themeIds: new UntypedFormControl([]),
      photos: new UntypedFormArray([], Validators.required),
      address: new UntypedFormControl('', this.formService.getValidators(65535)),
      detailAddress: new UntypedFormControl('', this.formService.getValidators(500)),
      howToCome: new UntypedFormControl('', Validators.maxLength(1000)),
      runningDays: new UntypedFormControl(
        '',
        Validators.compose([Validators.required, Validators.min(0), Validators.max(30)]),
      ),
      runningHours: new UntypedFormControl(
        '',
        Validators.compose([Validators.required, Validators.min(0), Validators.max(23)]),
      ),
      runningMinutes: new UntypedFormControl(
        '',
        Validators.compose([Validators.required, Validators.min(0), Validators.max(50)]),
      ),
      reservationHours: new UntypedFormControl(
        '',
        Validators.compose([Validators.required, Validators.min(6), Validators.max(RESERVATION_HOURS_MAX)]),
      ),
      price: new UntypedFormControl(
        '',
        this.formService.getValidators(10, [
          Validators.min(0),
          Validators.max(100000000),
          Validators.pattern('^[0-9]*$'),
        ]),
      ),
      // 할인 쿠폰 지정은 운영팀만 한다(NNB-912) — 호스트 화면에는 입력칸이 없다.
      recommend: new UntypedFormControl('', Validators.compose([Validators.required, Validators.maxLength(500)])),
      description: new UntypedFormControl(''),
      courses: new UntypedFormArray([]),
      notice: new UntypedFormControl('', Validators.maxLength(500)),
      checkList: new UntypedFormControl('', this.formService.getValidators(500)),
      includeList: new UntypedFormControl('', Validators.maxLength(500)),
      excludeList: new UntypedFormControl('', Validators.maxLength(500)),
      amenities: new UntypedFormArray([]),
      isAmenity: new UntypedFormControl(),
      refundPolicy: new UntypedFormControl('', this.formService.getValidators(1000)),
    });

    // Check if this is a rejection redirect from notification
    this.route.queryParams.subscribe((queryParams) => {
      if (queryParams.showRejection === 'true') {
        const productId = this.route.snapshot.params.id;
        // Redirect to products page with the rejection modal params
        this.router.navigate(['/host/products'], {
          queryParams: {
            productId: productId,
            showRejection: 'true',
          },
        });
        return;
      }
    });

    this.route.params.subscribe((params) => {
      this.productService.getRefinedProduct(+params.id).subscribe(
        (resp) => {
          this.meeting = resp;
          this.loadReservableTicketState(this.meeting.id);
          this.themesLoaded = this.meeting.themes !== undefined;
          this.themeIds = (this.meeting.themes ?? []).map((theme) => theme.id);
          this.loadedThemeIds = [...this.themeIds];
          this.productContext = this.buildProductContext(this.meeting);
          this.aiService.getAiAccess().subscribe(
            (access) => {
              this.aiAdvisorEnabled = access.aiProductAdvisor;
              // *ngIf 로 ai-chat 이 렌더된 다음 사이클에 앵커를 body 로 이동
              if (access.aiProductAdvisor) {
                this.aiChatMoveTimer = setTimeout(() => this.moveAiChatToBody(), 0);
              }
            },
            (error) => {
              // 삼키면 401/5xx/네트워크 오류가 전부 "권한 없음"으로 뭉개진다.
              this.aiAdvisorEnabled = false;
              captureSurfacedFailure(error, { feature: 'ai-advisor', phase: 'access-check' });
            },
          );
          if (this.meeting.version === 1) {
            this.productForm.addControl(
              'point',
              new UntypedFormControl('', Validators.compose([Validators.required, Validators.maxLength(500)])),
            );
          } else if (this.meeting.version === 2) {
            this.productForm.addControl('summaries', new UntypedFormArray([]));
            this.productForm.addControl('sections', new UntypedFormArray([]));
          }
          this.setFormControlsValue(this.meeting);
          this.loadMap(this.meeting);
          // 초기 로딩 완료 후 자동 저장 설정 (초기 valueChanges 이벤트 무시)
          setTimeout(() => {
            this.setupAutoSave();
          }, 0);
          this.checkRejectionStatus();
        },
        (error) => {
          if ((error as HttpErrorResponse).status === 404) {
            this.messageService.popup({
              title: '상품 수정',
              description: '상품이 존재하지 않습니다.',
              cancelLabel: '확인',
            });
            this.router.navigate(['/host/products']);
          }
        },
      );
    });
  }

  private buildProductContext(product: Product): ProductContext {
    return {
      productId: product.id,
      title: product.title,
      price: product.price,
      discountPrice: product.discountPrice,
      description: product.description,
      recommend: product.recommend,
      point: product.point,
      location: {
        address: product.address,
        detailAddress: product.detailAddress,
        sido: product.sido,
        sigugun: product.sigugun,
        country: product.country,
        lat: product.lat,
        lon: product.lon,
        howToCome: product.howToCome,
      },
      duration: {
        days: product.runningDays,
        hours: product.runningHours,
        minutes: product.runningMinutes,
      },
      policies: {
        refundPolicy: product.refundPolicy,
        notice: product.notice,
        checkList: product.checkList,
        includeList: product.includeList,
        excludeList: product.excludeList,
      },
      summaries:
        product.summaries?.map((s) => ({
          title: s.title,
          description: s.description,
          photos: s.photos?.map((p) => p) || [],
        })) || [],
      sections:
        product.sections?.map((s) => ({
          title: s.title,
          paragraphs:
            s.paragraphs?.map((p) => ({
              title: p.title,
              description: p.description,
              photos: p.photos?.map((ph) => ph) || [],
              time: p.time,
              note: p.note,
            })) || [],
        })) || [],
      hashtags: product.hashtags || [],
      representationPhotos:
        product.representationPhotos?.map((p) => ({
          photo: p.photo,
        })) || [],
    };
  }

  /**
   * AI 제안의 주소를 좌표로 바꾼다. 규약(네이버 → 구글 → 역지오코딩)은 GeocodeService 한 곳에 있다.
   * 예전에는 이 메서드가 페이지마다 복붙돼 있었고 country 를 '대한민국' 으로 하드코딩하거나
   * 아예 채우지 않아, 어느 쪽도 해외 주소를 받을 수 없었다.
   */
  private async geocodeAddress(address: string): Promise<any> {
    const outcome = await this.geocodeService.geocode(address);
    if (outcome.kind !== 'hits') throw new Error(`Geocoding failed: ${outcome.kind}`);
    const hit = outcome.hits[0];
    // 근사치·지역 미확인을 조용히 넘기면 담당자는 "성공"으로 보고 틀린 좌표를 저장한다.
    // 이 경로에는 드래그 핀이 보이지 않으므로 알림이 유일한 신호다.
    if (hit.approximate) {
      this.messageService.toast('번지까지 찾지 못해 대략적인 위치입니다. 지도에서 핀을 끌어 위치를 확인해주세요.');
    }
    if (hit.regionUnresolved) {
      this.messageService.toast('시·군·구를 채우지 못했습니다. 주소를 더 정확히 입력해주세요.');
    }
    return {
      // 해외 결과는 주소 원문을 덮지 않는다(normalizedAddress 가 null).
      address: hit.normalizedAddress ?? address,
      latitude: hit.latitude,
      longitude: hit.longitude,
      country: hit.country,
      sido: hit.sido,
      sigugun: hit.sigugun,
      dongmyun: hit.dongmyun,
      ri: hit.ri,
      rest: hit.rest,
    };
  }

  async onAiProductUpdate(proposal: Partial<ProductContext>) {
    if (!this.productForm) {
      return;
    }

    const { proposal: updatedData, dropped } = dropDescriptionOverRichHtml(
      proposal,
      this.productForm.value.description,
    );
    if (dropped && Object.values(updatedData).every((value) => value === undefined)) {
      this.messageService.toast(RICH_HTML_AI_SKIPPED_MESSAGE);
      return;
    }

    if (updatedData.location) {
      const { address, detailAddress, howToCome } = updatedData.location;
      // detailAddress/howToCome 만 제안된 경우에도 유실되지 않도록 먼저 patch
      this.productForm.patchValue({
        detailAddress: detailAddress || this.productForm.value.detailAddress,
        howToCome: howToCome || this.productForm.value.howToCome,
      });
      // 주소가 있을 때만 geocode 로 좌표/주소 갱신
      if (address) {
        try {
          const geoResult = await this.geocodeAddress(address);
          if (geoResult) {
            this.changeAddress(geoResult);
            this.productForm.patchValue({ address: geoResult.address });
          }
        } catch (e) {
          this.messageService.toast('주소를 좌표로 변환하는데 실패했습니다. 직접 주소를 검색해주세요.');
        }
      }
      delete updatedData.location;
    }

    // 1. 대표 이미지 (representationPhotos) 처리
    if (updatedData.representationPhotos) {
      const newPhotosArray = this.fb.array(
        updatedData.representationPhotos.map((p) => this.fb.group({ photo: p.photo })),
        Validators.required,
      );
      this.productForm.setControl('photos', newPhotosArray);
      delete updatedData.representationPhotos;
    }

    // 2. 요약 (summaries) 처리 — version 2 에만 존재
    if (updatedData.summaries) {
      const summariesArray = this.productForm.get('summaries') as UntypedFormArray;
      if (summariesArray) {
        while (summariesArray.length) {
          summariesArray.removeAt(0);
        }
        updatedData.summaries.forEach((summary) => {
          const photoArray = this.fb.array(
            summary?.photos?.map((photoUrl) => this.fb.group({ photo: photoUrl })) ?? [],
          );
          summariesArray.push(
            this.fb.group({
              photos: photoArray,
              title: [summary.title, this.formService.getValidators(50)],
              description: [summary.description, this.formService.getValidators(200)],
            }),
          );
        });
      }
      delete updatedData.summaries;
    }

    // 3. 상세 섹션 (sections) 처리 — version 2 에만 존재
    if (updatedData.sections) {
      const sectionsArray = this.productForm.get('sections') as UntypedFormArray;
      if (sectionsArray) {
        while (sectionsArray.length) {
          sectionsArray.removeAt(0);
        }
        updatedData.sections.forEach((section) => {
          const paragraphsArray = this.fb.array(
            section.paragraphs.map((paragraph) => {
              const photoArray = this.fb.array(
                paragraph?.photos?.map((photoUrl) => this.fb.group({ photo: photoUrl })) ?? [],
              );
              return this.fb.group({
                title: [paragraph.title, this.formService.getValidators(50)],
                time: [paragraph.time, Validators.maxLength(50)],
                photos: photoArray,
                description: [paragraph.description, Validators.required],
                note: [paragraph.note],
              });
            }),
            Validators.required,
          );
          sectionsArray.push(
            this.fb.group({
              title: [section.title, this.formService.getValidators(50)],
              paragraphs: paragraphsArray,
            }),
          );
        });
      }
      delete updatedData.sections;
    }

    // 4. 나머지 필드는 patchValue로 업데이트
    this.productForm.patchValue(updatedData, { emitEvent: false });

    // 후속 AI 대화가 최신 폼 상태를 기준으로 동작하도록 productContext 동기화
    if (this.meeting) {
      this.productContext = this.buildProductContext({ ...this.meeting, ...this.productForm.value });
    }

    this.messageService.toast(dropped ? RICH_HTML_AI_PARTIAL_MESSAGE : 'AI가 제안한 내용으로 폼이 업데이트되었습니다.');
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    // 초기 진입 시 이미 앵커가 있으면 이동 (대개 access 응답 후 *ngIf 로 늦게 렌더됨)
    this.moveAiChatToBody();
  }

  /**
   * ai-chat 앵커(.host-ai-chat-anchor)를 document.body 로 이동시켜
   * mat-sidenav-content 의 stacking context 밖으로 빼낸다. 좌측 하단 fixed 버튼이
   * 좌측 메뉴 위에 표시된다. 중복 이동/미렌더 상태를 가드한다.
   */
  private moveAiChatToBody(): void {
    if (this.isDestroyed || this.aiChatMovedToBody) return;
    const anchor = this.aiChatAnchor?.nativeElement;
    if (anchor && anchor.parentElement !== document.body) {
      document.body.appendChild(anchor);
      this.aiChatMovedToBody = true;
    }
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    if (this.aiChatMoveTimer) {
      clearTimeout(this.aiChatMoveTimer);
    }
    if (this.autoSaveSubscription) {
      this.autoSaveSubscription.unsubscribe();
    }
    if (this.formChangeSubscription) {
      this.formChangeSubscription.unsubscribe();
    }
    // body 로 옮긴 앵커는 Angular 가 자동 제거하지 못하므로 수동 정리
    const anchor = this.aiChatAnchor?.nativeElement;
    if (anchor && anchor.parentElement === document.body) {
      document.body.removeChild(anchor);
    }
  }

  private setupAutoSave() {
    // 초기 로딩 완료 플래그
    let isInitialLoad = true;

    // 변경 감지: 폼 값이 변경되면 hasUnsavedChanges를 true로 설정
    this.formChangeSubscription = this.productForm.valueChanges.subscribe(() => {
      // 초기 로딩 시에는 변경사항으로 간주하지 않음
      if (!isInitialLoad) {
        this.hasUnsavedChanges = true;
        this.formRevision++;
      }
    });

    // 자동 저장: 5초 후 자동 저장
    this.autoSaveSubscription = this.productForm.valueChanges.pipe(debounceTime(5000)).subscribe(() => {
      // 초기 로딩 플래그 해제
      isInitialLoad = false;
      this.autoSave();
    });

    // 다음 이벤트 루프에서 초기 로딩 플래그 해제
    setTimeout(() => {
      isInitialLoad = false;
    }, 100);
  }

  private async autoSave() {
    if (this.isAutoSaving) {
      this.autoSavePending = true;
      return;
    }
    if (this.isLoad || !this.hasUnsavedChanges) {
      return;
    }

    this.isAutoSaving = true;
    const revision = this.formRevision;
    try {
      const dto = await this.buildProductDto();
      this.productService
        .updateProduct(this.meeting.id, dto, this.meeting.sortOrder)
        .pipe(failIfSwallowed())
        .subscribe(
          (product) => {
            this.markThemesSaved(dto.themes);
            this.lastAutoSaved = new Date();
            this.markSavedIfUnchanged(revision);
            this.autoSaveFailureNotified = false;
            this.messageService.toast('자동 저장되었습니다');
            this.finishAutoSave();
          },
          (error) => {
            // 자동저장 실패는 조용히 무시한다 — 다음 변경에서 다시 시도된다.
            // 단 "본문은 저장됐고 테마만 실패"는 다시 시도해도 같은 이유로 계속 실패하므로,
            // 호스트가 테마를 고치도록 사유를 알린다(조용히 무시하면 왜 안 붙는지 알 길이 없다).
            const themeError = toThemeUpdateErrorMessage(error);
            if (themeError) {
              this.messageService.toast(`테마는 저장되지 않았어요: ${themeError}`);
            }
            this.finishAutoSave();
          },
        );
    } catch (error) {
      const reason = this.reportPreRequestSaveFailure(error, 'autosave');
      if (!this.autoSaveFailureNotified) {
        this.autoSaveFailureNotified = true;
        this.messageService.toast(`자동 저장하지 못했습니다. 저장 버튼으로 다시 시도해주세요. (${reason})`);
      }
      this.finishAutoSave();
    }
  }

  private finishAutoSave() {
    this.isAutoSaving = false;
    if (this.autoSavePending) {
      this.autoSavePending = false;
      this.autoSave();
    }
  }

  /** 저장 요청을 보낸 뒤 들어온 수정이 있으면 "저장됨"으로 덮지 않는다. */
  private markSavedIfUnchanged(revision: number) {
    if (this.formRevision !== revision) return;
    this.hasUnsavedChanges = false;
    this.productForm.markAsPristine();
  }

  private reportPreRequestSaveFailure(error: unknown, phase: 'autosave' | 'save' | 'edit'): string {
    captureSurfacedFailure(error, {
      feature: 'host-product-edit-save',
      phase,
      extra: { productId: this.meeting?.id },
    });
    return error instanceof Error ? error.message : String(error);
  }

  private showPreRequestSaveFailure(error: unknown, phase: 'save' | 'edit') {
    const reason = this.reportPreRequestSaveFailure(error, phase);
    this.messageService.popup({
      title: '저장하지 못했습니다',
      description: `저장을 준비하는 중 문제가 생겨 요청이 전송되지 않았습니다.\n작성하신 내용은 화면에 그대로 있으니 창을 닫지 마시고 다시 시도해주세요.\n\n(${reason})`,
      cancelLabel: '확인',
    });
  }

  /**
   * 테마 칩 선택 반영. 폼 컨트롤을 거쳐야 `valueChanges` 가 울려 자동 저장이 돌고,
   * `markAsDirty` 를 해야 이탈 경고(`CanDeactivateGuard` 가 보는 `productForm.dirty`)가 걸린다.
   * 둘 다 없으면 테마만 고치고 페이지를 벗어났을 때 경고도 저장도 없이 사라진다.
   */
  onThemeIdsChange(themeIds: number[]) {
    this.themeIds = themeIds;
    this.productForm.controls.themeIds.setValue(themeIds);
    this.productForm.markAsDirty();
  }

  /**
   * 저장에 성공하면 **그 요청이 실제로 보낸 값**을 새 기준으로 삼는다 — 다음 저장에서 다시 보내지 않도록.
   *
   * 지금의 `themeIds` 가 아니라 보낸 값을 받는 이유는 `buildProductDto` 가 S3 업로드를 await 해서
   * DTO 를 만든 뒤 응답이 올 때까지 시간이 뜨기 때문이다. 그 사이 호스트가 테마를 또 바꾸면
   * 현재 값을 기준으로 삼는 순간 "이미 저장됨"으로 오인해 그 변경이 영영 안 나간다.
   * 안 보낸 저장(`undefined`)이면 기준을 그대로 둔다.
   */
  private markThemesSaved(sentThemeIds?: number[]) {
    if (sentThemeIds === undefined) return;
    this.loadedThemeIds = [...sentThemeIds];
  }

  private setFormControlsValue(product: Product) {
    const noti = product.notice ? product.notice.replace(/<br>/g, '\n') : product.notice;
    const check = product.checkList ? product.checkList.replace(/<br>/g, '\n') : product.checkList;
    const inc = product.includeList ? product.includeList.replace(/<br>/g, '\n') : product.includeList;
    const ex = product.excludeList ? product.excludeList.replace(/<br>/g, '\n') : product.excludeList;
    this.productForm.controls.version.setValue(product.version);
    this.productForm.controls.title.setValue(product.title);
    this.productForm.controls.hashtags.setValue(product.hashtags.filter((h) => !h.isAnalysis).map((h) => h.id));
    this.productForm.controls.themeIds.setValue(this.themeIds);
    const photoArray = this.productForm.controls.photos as UntypedFormArray;
    if (product.representationPhotos && product.representationPhotos.length > 0) {
      product.representationPhotos.forEach((photo, index) => {
        photoArray.setControl(index, new UntypedFormControl(photo, Validators.required));
      });
    }
    this.productForm.controls.address.setValue(product.address);
    this.productForm.controls.detailAddress.setValue(product.detailAddress);
    this.productForm.controls.howToCome.setValue(product.howToCome);
    this.productForm.controls.runningDays.setValue(product.runningDays);
    this.productForm.controls.runningHours.setValue(product.runningHours);
    this.productForm.controls.runningMinutes.setValue(product.runningMinutes);
    this.productForm.controls.reservationHours.setValue(product.reservationHours);
    this.productForm.controls.price.setValue(product.price);
    this.productForm.controls.recommend.setValue(product.recommend);
    this.productForm.controls.description.setValue(product.description);
    const amenityArray = this.productForm.controls.amenities as UntypedFormArray;
    product.amenities.forEach((amenity) => {
      amenityArray.push(new UntypedFormControl(amenity.id));
    });
    this.productForm.controls.isAmenity.setValue(product.amenities?.length > 0);
    const courseArray = this.productForm.controls.courses as UntypedFormArray;
    const courseGroup = this.utilService.groupBy(product.courses, (course) => course.title);
    Object.keys(courseGroup).forEach((key) => {
      courseArray.push(
        new UntypedFormGroup({
          title: new UntypedFormControl(key, this.formService.getValidators(20)),
          schedules: new UntypedFormArray(
            courseGroup[key]?.map((element) => {
              return new UntypedFormGroup({
                subTitle: new UntypedFormControl(element.subTitle, this.formService.getValidators(20)),
                content: new UntypedFormControl(element.content, Validators.required),
                note: new UntypedFormControl(element.note),
              });
            }),
          ),
        }),
      );
    });
    this.productForm.controls.notice.setValue(noti);
    this.productForm.controls.checkList.setValue(check);
    this.productForm.controls.includeList.setValue(inc);
    this.productForm.controls.excludeList.setValue(ex);
    this.productForm.controls.refundPolicy.setValue(product.refundPolicy);

    if (this.meeting.version === 1) {
      this.productForm.controls.point.setValue(product.point);
    } else if (this.meeting.version === 2) {
      const summaryArray = this.productForm.controls.summaries as UntypedFormArray;
      product.summaries?.forEach((summary) => {
        const photoArray = new UntypedFormArray([]);
        summary.photos?.forEach((photo, index) => {
          photoArray.push(
            new UntypedFormGroup({
              photo: new UntypedFormControl(photo),
            }),
          );
        });
        summaryArray.push(
          new UntypedFormGroup({
            photos: photoArray,
            title: new UntypedFormControl(summary.title, this.formService.getValidators(50)),
            description: new UntypedFormControl(summary.description, this.formService.getValidators(200)),
          }),
        );
      });
      const sectionArray = this.productForm.controls.sections as UntypedFormArray;
      product.sections?.forEach((section) => {
        const paragraphArray = new UntypedFormArray([], Validators.required);
        section.paragraphs?.forEach((paragraph) => {
          const photoArray = new UntypedFormArray([]);
          paragraph.photos?.forEach((photo, index) => {
            photoArray.push(
              new UntypedFormGroup({
                photo: new UntypedFormControl(photo),
              }),
            );
          });
          paragraphArray.push(
            new UntypedFormGroup({
              title: new UntypedFormControl(paragraph.title, this.formService.getValidators(50)),
              time: new UntypedFormControl(paragraph.time, Validators.maxLength(50)),
              photos: photoArray,
              description: new UntypedFormControl(paragraph.description, Validators.required),
              note: new UntypedFormControl(paragraph.note),
            }),
          );
        });
        sectionArray.push(
          new UntypedFormGroup({
            title: new UntypedFormControl(section.title, this.formService.getValidators(50)),
            paragraphs: paragraphArray,
          }),
        );
      });
    }
  }

  loadMap(meeting: Product) {
    this.latitude = meeting.lat || this.SEOUL_LATITUDE;
    this.longitude = meeting.lon || this.SEOUL_LONGITUDE;
    this.zoom = 15;
  }

  private readonly photoUploaders = new Map<imageResizeOption, PhotoUploader>();

  /** 옵션별로 같은 함수를 돌려줘야 동시에 도는 자동·수동 저장이 한 번의 업로드를 나눠 쓴다. */
  private photoUploader(resize: imageResizeOption): PhotoUploader {
    const cached = this.photoUploaders.get(resize);
    if (cached) return cached;
    const uploader: PhotoUploader = async (file) => {
      const uploadResp = await this.s3Service.uploadFile(file, environment.folder.meeting);
      if (!uploadResp?.key) throw new Error('S3 업로드 응답에 key가 없습니다');
      const resizeResp = await this.s3Service.resizeImage(uploadResp.key, 'meetings-resized', resize);
      if (!resizeResp?.url) throw new Error('이미지 리사이즈 응답에 URL이 없습니다');
      return resizeResp.url;
    };
    this.photoUploaders.set(resize, uploader);
    return uploader;
  }

  private async buildProductDto(): Promise<ProductUpdateDto> {
    const {
      version,
      title,
      hashtags,
      photos,
      address,
      detailAddress,
      howToCome,
      runningDays,
      runningHours,
      runningMinutes,
      reservationHours,
      price,
      point,
      recommend,
      summaries,
      description,
      sections,
      courses,
      amenities,
      notice,
      checkList,
      includeList,
      excludeList,
      refundPolicy,
    } = this.productForm.value;
    const hashtagNames = hashtags
      .map((tag) => {
        let id = undefined;
        let name = undefined;
        if (typeof tag === 'object' && tag !== null) {
          id = tag.id;
          name = tag.name || tag.label;
        } else if (typeof tag === 'number') {
          id = tag;
        } else if (typeof tag === 'string') {
          name = tag;
        }
        if (id) {
          return new HashtagCreateDto(id, name, HashtagType.PRODUCT, false);
        } else if (name) {
          return new HashtagCreateDto(undefined, name, HashtagType.PRODUCT, false);
        }
        return null;
      })
      .filter((item) => item !== null);

    if (version === 2) {
      const uploadFixedHeight = this.photoUploader(FIXED_HEIGHT_PHOTO_RESIZE);
      const uploadSquare = this.photoUploader(SQUARE_PHOTO_RESIZE);
      for (const [i, summary] of summaries.entries()) {
        summary.photos = await toStoredPhotoUrls(summary.photos, uploadFixedHeight, `상세요약 ${i + 1}`);
      }
      for (const [i, section] of sections.entries()) {
        for (const [j, paragraph] of section.paragraphs.entries()) {
          const label = `상세 섹션 ${i + 1}의 문단 ${j + 1}`;
          paragraph.photos = await toStoredPhotoUrls(paragraph.photos, uploadSquare, label);
        }
      }
    }
    const courseList = [];
    courses?.forEach((course) => {
      course.schedules?.forEach((schedule) => {
        courseList.push(new CourseCreateDto(course.title, schedule.subTitle, schedule.content, schedule.note));
      });
    });
    const photoUrls = await toStoredPhotoUrls(photos, this.photoUploader(SQUARE_PHOTO_RESIZE), '대표 이미지');
    const photoDtos = photoUrls.map((url) => new ProductRepresentationPhotoCreateDto(url, 0));

    const dto = {
      version,
      title,
      point,
      recommend,
      summaries,
      description,
      sections,
      courses: courseList,
      address,
      detailAddress,
      howToCome,
      runningDays,
      runningHours,
      runningMinutes,
      reservationHours,
      price: +price,
      lat: this.latitude,
      lon: this.longitude,
      country: this.country,
      sido: this.sido,
      sigugun: this.sigugun,
      dongmyun: this.dongmyun,
      ri: this.ri,
      rest: this.rest,
      notice,
      checkList,
      includeList,
      excludeList,
      refundPolicy,
      status: this.meeting.status,
      isBusiness: this.meeting.isBusiness,
      hashtags: hashtagNames,
      amenities,
      representationPhotos: photoDtos,
      isOffline: this.meeting.isOffline,
    } as ProductUpdateDto;

    // 테마는 서버가 내려준 적이 있고(themesLoaded) 실제로 바뀌었을 때만 싣는다.
    // `undefined`면 서버가 매핑을 건드리지 않으므로, 모를 때 보내지 않는 것이 곧 방어다 —
    // 화면이 서버보다 먼저 배포되면 구버전 응답에 themes 가 없는데 빈 배열로 보내면 전체 해제가 된다.
    if (this.themesLoaded && hasThemeSelectionChanged(this.themeIds, this.loadedThemeIds)) {
      dto.themes = this.themeIds;
    }

    return dto;
  }

  async saveProduct() {
    if (this.productForm.invalid) {
      this.showValidationErrors();
      return;
    }
    this.isLoad = true;
    const revision = this.formRevision;
    try {
      const dto = await this.buildProductDto();
      this.productService
        .updateProduct(this.meeting.id, dto, this.meeting.sortOrder)
        .pipe(failIfSwallowed())
        .subscribe(
          (product) => {
            this.markThemesSaved(dto.themes);
            this.markSavedIfUnchanged(revision);
            this.messageService.toast('저장되었습니다');
            this.isLoad = false;
          },
          (error) => {
            const themeError = toThemeUpdateErrorMessage(error);
            this.messageService.popup({
              title: '상품 저장',
              description: themeError
                ? `상품은 저장됐지만 테마는 저장되지 않았어요.\n(${themeError})`
                : '저장에 실패했습니다. 다시 시도해주세요.',
              cancelLabel: '확인',
            });
            console.log(error);
            this.isLoad = false;
          },
        );
    } catch (error) {
      this.isLoad = false;
      this.showPreRequestSaveFailure(error, 'save');
    }
  }

  async edit() {
    this.isLoad = true;
    try {
      const dto = await this.buildProductDto();
      this.productService
        .updateProduct(this.meeting.id, dto, this.meeting.sortOrder)
        .pipe(failIfSwallowed())
        .subscribe(
          (product) => {
            this.markThemesSaved(dto.themes);
            this.messageService.popup({
              title: '상품 수정',
              description: '상품을 수정하였습니다.',
              cancelLabel: '확인',
            });
            this.passDeactivate = true;
            this.isLoad = false;
            this.router.navigate(['/host/products']);
            this.productForm.reset();
          },
          (error) => {
            // 테마만 실패한 경우 상품 본문은 이미 저장돼 있다. 백업 안내를 그대로 띄우면
            // 호스트가 저장된 내용을 날아간 줄 알고 다시 입력한다.
            const themeError = toThemeUpdateErrorMessage(error);
            this.messageService.popup({
              title: '상품 수정',
              description: themeError
                ? `상품은 수정됐지만 테마는 저장되지 않았어요. 테마를 다시 선택해주세요.\n(${themeError})`
                : '상품 수정에 문제가 발생했습니다. 작성하신 내용이 저장되지 않을 수 있으니 백업 하시고 노는법에게 문의주세요.',
              cancelLabel: '확인',
            });
            console.log(error);
            this.isLoad = false;
          },
        );
    } catch (error) {
      this.isLoad = false;
      this.showPreRequestSaveFailure(error, 'edit');
    }
  }

  requestReview() {
    // 검수 대기 중이면 취소 로직 실행
    if (this.meeting.status === ProductStatus.PENDING_REVIEW) {
      this.messageService.popup({
        title: '검수 취소',
        description: '검수 요청을 취소하시겠습니까?\n취소 후 상품을 수정하고 다시 검수 요청할 수 있습니다.',
        confirmLabel: '취소하기',
        confirmBtnClass: 'warning',
        confirmHandler: () => {
          this.isLoad = true;
          this.productService.cancelReview(this.meeting.id).subscribe(
            (product) => {
              this.meeting = product;
              this.messageService.toast('검수 요청이 취소되었습니다');
              this.isLoad = false;
            },
            (error) => {
              this.messageService.popup({
                title: '검수 취소 실패',
                description: '검수 취소에 실패했습니다. 다시 시도해주세요.',
                cancelLabel: '확인',
              });
              this.isLoad = false;
            },
          );
        },
        cancelLabel: '돌아가기',
      });
      return;
    }

    // 검수 요청 로직
    if (this.productForm.invalid) {
      this.showValidationErrors();
      return;
    }

    this.messageService.popup({
      title: '검수 요청',
      description:
        '상품을 검수 요청하시겠습니까?\n승인 후 고객에게 노출됩니다.\n\n승인은 영업일 기준 3~5일 정도 소요됩니다.',
      confirmLabel: '요청하기',
      confirmBtnClass: 'primary',
      confirmHandler: () => {
        this.isLoad = true;
        this.productService.requestReview(this.meeting.id).subscribe(
          (product) => {
            this.meeting = product;
            this.messageService.popup({
              title: '검수 요청 완료',
              description: '검수 요청이 완료되었습니다. 승인 후 알림을 보내드립니다.',
              cancelLabel: '확인',
              cancelHandler: () => {
                this.router.navigate(['/host/products']);
              },
            });
            this.isLoad = false;
          },
          (error) => {
            this.messageService.popup({
              title: '검수 요청 실패',
              description: toReviewRequestErrorMessage(error),
              cancelLabel: '확인',
            });
            this.isLoad = false;
          },
        );
      },
      cancelLabel: '취소',
    });
  }

  /**
   * 연락처 인증을 이 화면에서 끝낸다. `/host/info`로 보내면 작성 중이던 상품을 두고 떠나야 하고,
   * 돌아와도 자동 저장 debounce에 걸린 변경이 유실될 수 있다.
   * 바텀시트는 host-info 페이지가 쓰는 것과 동일한 컴포넌트이며, `target: 'host_info'`라
   * `user.mobile`이 아니라 호스트 알림 수신 번호만 갱신한다.
   */
  openMobileVerification() {
    const initialPhone = this.hostInfo?.mobileUnverified || this.hostInfo?.mobile || undefined;
    const ref = this.bottomSheet.open<
      MobileVerificationBottomSheetComponent,
      MobileVerificationBottomSheetData,
      MobileVerificationResult
    >(MobileVerificationBottomSheetComponent, {
      data: { initialPhone, target: 'host_info' },
      panelClass: 'mobile-verification-sheet-panel',
    });
    ref.afterDismissed().subscribe((result) => {
      if (result?.verified) {
        this.isMobileVerified = true;
        if (this.hostInfo) {
          this.hostInfo.mobile = result.e164Phone ?? this.hostInfo.mobile;
        }
      }
    });
  }

  /**
   * 예약 가능한 일정이 있는지 서버에 물어 안내 노출 여부를 정한다(NNB-679).
   *
   * 조회가 실패하면 안내를 띄우지 않는 쪽(true)으로 둔다 — 일정을 등록해 둔 호스트에게 잘못된 경고를
   * 보이는 것보다 서버 게이트가 걸러 주는 편이 낫다. 502는 인터셉터가 `EMPTY`로 삼켜 `error`도 돌지 않으므로
   * `failIfSwallowed()`로 에러로 되살려 Sentry까지 보낸다.
   *
   * 실패 처리가 두 갈래라 불리언 대신 유니온을 받는다 — 잘못된 값이 타입 오류가 되어야, 재조회가
   * 조용히 `'hide'` 로 넘어가 게이트가 성공을 단정하는 일이 생기지 않는다.
   */
  private loadReservableTicketState(productId: number, onFailure: 'hide' | 'keep' = 'hide') {
    // 다른 상품으로 넘어온 경우 이전 상품의 값이 남지 않게 조회마다 초기화하고, 늦게 도착한 응답은 버린다.
    if (onFailure === 'hide') this.hasReservableTicket = true;
    this.productService
      .getHasReservableTicket(productId)
      .pipe(failIfSwallowed())
      .subscribe({
        next: ({ hasReservableTicket }) => {
          if (this.meeting?.id === productId) this.hasReservableTicket = hasReservableTicket;
        },
        error: (error) => {
          // next 와 같은 가드가 없으면, 상품 A 의 늦은 실패가 이미 표시 중인 상품 B 의 안내를 지운다.
          if (onFailure === 'hide' && this.meeting?.id === productId) this.hasReservableTicket = true;
          captureSurfacedFailure(error, {
            feature: 'host-product-edit',
            phase: 'load-reservable-ticket',
            extra: { productId },
          });
        },
      });
  }

  /**
   * 게이트가 일정을 고친 직후 판정을 다시 받는다 — 안내가 걷히는 근거는 서버 판정뿐이다.
   *
   * 재조회는 현재 값을 유지한 채 묻는다. `true` 로 되돌리면 응답이 오기 전까지, 그리고 조회가
   * 실패하면 영구히, 게이트의 마지막 단계가 "일정을 고쳤습니다"로 성공을 단정한다.
   */
  refreshReservableTicketState() {
    const productId = this.meeting?.id;
    if (productId) this.loadReservableTicketState(productId, 'keep');
  }

  private showValidationErrors() {
    const errors = [];
    if (this.productForm.controls.title.invalid) errors.push('상품 이름');
    if (this.productForm.controls.photos.invalid) errors.push('대표 이미지');
    if (this.productForm.controls.address.invalid) errors.push('주소');
    if (this.productForm.controls.detailAddress.invalid) errors.push('상세 주소');
    if (this.productForm.controls.howToCome.invalid) errors.push('오시는 방법');
    if (
      this.productForm.controls.runningDays.invalid ||
      this.productForm.controls.runningHours.invalid ||
      this.productForm.controls.runningMinutes.invalid
    )
      errors.push('소요시간');
    if (this.productForm.controls.price.invalid) errors.push('가격');
    if (this.productForm.controls.recommend.invalid) errors.push('추천 포인트');
    if (this.productForm.controls.description.invalid) errors.push('설명');
    if (this.productForm.controls.courses.invalid) errors.push('코스');
    if (this.productForm.controls.checkList.invalid) errors.push('준비물');

    this.messageService.popup({
      title: '입력 확인',
      description: `다음 항목을 확인해주세요:\n• ${errors.join('\n• ')}`,
      cancelLabel: '확인',
    });
  }

  private checkRejectionStatus() {
    // URL 쿼리 파라미터 확인
    this.route.queryParams.subscribe((params) => {
      const showRejection = params['showRejection'] === 'true';

      // 상품이 DISABLED 상태이고 productConfirms가 있으면 반려 사유 확인
      if (
        this.meeting.status === ProductStatus.DISABLED &&
        this.meeting.productConfirms &&
        this.meeting.productConfirms.length > 0
      ) {
        // isConfirm === false로 찾던 것이 원인이 되어 아무것도 매칭되지 않았다 — 반려는 isConfirm에
        // 값을 남기지 않아 NULL로 저장돼 왔다. 판정 규칙은 util로 분리해 단위 테스트로 고정했다.
        const latestRejection = findActiveRejection(this.meeting.productConfirms);

        if (latestRejection && latestRejection.rejectComment) {
          // showRejection 파라미터가 있거나 DISABLED 상태면 모달 표시
          if (showRejection) {
            this.messageService.popup({
              title: '상품 반려 알림',
              description: `상품이 반려되었습니다.\n\n반려 사유:\n${latestRejection.rejectComment}\n\n수정 후 다시 검수 요청해주세요.`,
              cancelLabel: '확인',
            });
          }
        }
      }
    });
  }
}
